// Shared keyboard handling for underline tab bars, per the WAI-ARIA tabs
// pattern: Left/Right arrows move between tabs (wrapping), Home/End jump to
// first/last. Attach to the `role="tablist"` container's onKeyDown; pair with
// a roving tabindex (active tab tabIndex=0, others -1). Uses automatic
// activation — arrowing to a tab selects it and moves focus, matching how the
// campaign + Marketing panels keep every panel mounted (switching is cheap).
import type { KeyboardEvent } from "react";

export function handleTablistKeyDown<T extends string>(
  e: KeyboardEvent<HTMLElement>,
  ids: readonly T[],
  activeId: T,
  select: (id: T) => void | boolean,
): void {
  if (ids.length === 0) return;
  const idx = Math.max(0, ids.indexOf(activeId));
  let next: number;
  switch (e.key) {
    case "ArrowRight":
      next = (idx + 1) % ids.length;
      break;
    case "ArrowLeft":
      next = (idx - 1 + ids.length) % ids.length;
      break;
    case "Home":
      next = 0;
      break;
    case "End":
      next = ids.length - 1;
      break;
    default:
      return;
  }
  e.preventDefault();
  // A guarded transition (for example, leaving an unsaved scheduled edit)
  // can be declined. Keep focus on the selected tab in that case.
  if (select(ids[next]) === false) return;
  // The handler lives on the tablist container, so its tabs are queryable
  // children — focus follows selection (roving tabindex).
  const tabs = e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]');
  tabs[next]?.focus();
}
